var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { COPY_TEXT_EVENT_DESCRIPTION, DENIED_OR_WARNED_EVENT_NAME, } from '../constants/analytics';
import { getTextFromComponent } from './general';
import { getAPIErrorData, isMultiShipmentAddressError, isProtectionPlanError, } from './api-helpers';
import { defaultPageInfo, pushEventInfo, setPageDetails, } from './analytics-generic';
import { RETURN_METHOD } from '../constants/general';
import PrintLabelAlerts from '../components/print-return-label/PrintLabelAlerts';
import { finalizeReturnErrorMessage } from '../components/finalize-return/FinalizeReturnError';
/**
 *****************************************************
 *  RETURN METHOD TAGGING
 *****************************************************
 */
/**
 * On-load tagging for the Return Method page
 */
export var TagReturnMethodLoad = function (militaryExchange) {
    var user = {
        profile: {
            exchange: militaryExchange,
        },
    };
    setPageDetails(defaultPageInfo, { user: user });
};
/**
 * Legacy Return Method tagging for In Store event
 */
export var TagReturnMethodInStore = function (orderDetailsV2, militaryExchange) {
    var _a;
    var user = {
        profile: {
            exchange: militaryExchange,
        },
    };
    var postOrderReturn = {
        item: ((_a = orderDetailsV2 === null || orderDetailsV2 === void 0 ? void 0 : orderDetailsV2.lineItems) === null || _a === void 0 ? void 0 : _a.map(function (item) { return ({
            returnMethod: 'in-store',
            productInfo: {
                sku: item.skuNumber || 0,
                superSku: item.omsId || 0,
                productName: item.brandName || '',
            },
            price: {
                basePrice: (orderDetailsV2 === null || orderDetailsV2 === void 0 ? void 0 : orderDetailsV2.subTotalAmount) || 0,
            },
            quantity: item.orginalOrderedQuantity,
        }); })) || [],
        transactionID: (orderDetailsV2 === null || orderDetailsV2 === void 0 ? void 0 : orderDetailsV2.orderNumber) || '',
    };
    var pageDetails = __assign(__assign({}, defaultPageInfo), { pageInfo: __assign(__assign({}, defaultPageInfo.pageInfo), { pageName: 'account>purchase history>order details>return items>in-store return instructions', pageLoadEvent: 'confirm return' }) });
    setPageDetails(pageDetails, { postOrderReturn: postOrderReturn, user: user });
};
/**
 * Return Method tagging
 */
export var TagReturnMethodContinue = function (method) {
    var _a;
    var returnMethodAnalyticsValue = (_a = {},
        _a[RETURN_METHOD.IN_STORE] = 'in store returns',
        _a[RETURN_METHOD.UPS_DROP_OFF] = 'ups drop off',
        _a[RETURN_METHOD.LORP] = 'large item pickup',
        _a);
    var methodChosen = returnMethodAnalyticsValue[method] || 'unknown return method';
    var tagObject = {
        category: {
            primaryCategory: 'button submission',
        },
        eventInfo: {
            eventName: 'my account settings',
        },
        myAccount: {
            settings: "purchase history: order details: return items: ".concat(methodChosen, ": continue button"),
        },
    };
    pushEventInfo(tagObject);
};
/**
 *****************************************************
 *  ITEMS TO RETURN TAGGING
 *****************************************************
 */
/**
 * Tagging for when the user is denied or warned in Items to Return
 * @param fraudType - "DENIED" | "WARNED" | "WARNEDINVALID"
 */
export var TagOnApprissDeniedOrWarned = function (fraudType) {
    var eventName = DENIED_OR_WARNED_EVENT_NAME[fraudType];
    var tagObject = {
        category: {
            primaryCategory: 'overlay',
        },
        eventInfo: {
            eventName: eventName,
        },
    };
    pushEventInfo(tagObject);
};
/**
 * Tagging for when the user submits the Item to Return form
 * @param items - items chosen to be returned.
 */
export var TagItemSelectButtonSubmission = function (items, method, militaryExchange) {
    var formattedSelectedItems = items.map(function (item) { return ({
        productInfo: {
            sku: item.sku,
        },
        qty: item.returnQuantity,
        returnReason: item.returnReasonText,
    }); });
    var methodChosen = method === RETURN_METHOD.IN_STORE ? 'in store ' : '';
    var tagObject = {
        category: {
            primaryCategory: 'button submission',
        },
        eventInfo: {
            eventName: 'my account settings',
        },
        myAccount: {
            settings: methodChosen + 'return quantity and reason selected',
        },
        return: {
            item: formattedSelectedItems,
        },
        exchange: militaryExchange,
    };
    pushEventInfo(tagObject);
};
/**
 * Tagging for when the user copies the text in the Fraud Alert Modal
 * @param fraudType - "DENIED" | "WARNED" | "WARNEDINVALID"
 */
export var TagOnCopyTextToClipboard = function (fraudType) {
    if (!fraudType)
        return;
    var analyticsDescription = COPY_TEXT_EVENT_DESCRIPTION[fraudType];
    var tagObject = {
        category: {
            primaryCategory: 'button submission',
        },
        eventInfo: {
            eventName: 'my account settings',
        },
        myAccount: {
            settings: analyticsDescription,
        },
    };
    pushEventInfo(tagObject);
};
/**
 *****************************************************
 *  POLICY VALIDATOR TAGGING
 *****************************************************
 */
/**
 * Tagging for when the user receives an error from the policy validator API.
 * @param errorResponse - Axios error response
 */
export var TagPolicyValidatorApiError = function (errorResponse) {
    /**
     * Check for non-standard errors first for policy validator such as
     *    Multi vendor address error
     *    Protection Plan association error
     */
    var isMultiShipmentError = isMultiShipmentAddressError(errorResponse);
    if (isMultiShipmentError) {
        var tagObject = {
            category: {
                primaryCategory: 'error',
            },
            eventInfo: {
                eventName: 'post page error',
                error: [
                    {
                        type: 'error',
                        displayType: 'inline',
                        pageSection: 'post page error',
                        message: 'This combination of items cannot be returned online. You can return an item at any The Home Depot store, or call Customer Support at 1-800-430-3376 if you have questions. See our Returns Policy for more information.',
                        code: 'MULTI_SHIPMENTS',
                    },
                ],
            },
        };
        pushEventInfo(tagObject);
        return;
    }
    var isProtectionPlan = isProtectionPlanError(errorResponse);
    if (isProtectionPlan) {
        var tagObject = {
            category: {
                primaryCategory: 'error',
            },
            eventInfo: {
                eventName: 'post page error',
                error: [
                    {
                        type: 'error',
                        displayType: 'inline',
                        pageSection: 'post page error',
                        message: "We're sorry. The items below can't be returned. See our Returns Policy for more options.",
                        code: 'SSR_HRTVPOLICY_ERR_4102',
                    },
                ],
            },
        };
        pushEventInfo(tagObject);
        return;
    }
    if ((errorResponse === null || errorResponse === void 0 ? void 0 : errorResponse.message) === 'ALL_INELIGIBLE_ITEMS') {
        // Tag analytics when all items are ineligible
        TagPolicyValidatorAllItemsIneligible();
        return;
    }
    // If no specific policy validator was found then check for standard network API errors
    TagApiError(errorResponse);
};
/**
 * Sends analytics tag for EACH item that is not eligible for return due to hrtv.
 * Only applies to partially eligible items, theres a separate tag if all are ineligible items.
 * @param hrtvLineItemEligibilities mapping of item eligibilities that were selected
 */
export var TagPolicyValidatorPartialItemErrors = function (hrtvLineItemEligibilities) {
    var ineligibleItems = Object.keys(hrtvLineItemEligibilities).filter(function (key) { return !hrtvLineItemEligibilities[key]; });
    var errorList = ineligibleItems.map(function () { return ({
        type: 'error',
        displayType: 'inline',
        pageSection: 'post page error',
        message: "This item can't be returned online. You can return an item at any The Home Depot store, or call Customer Support at 1-800-430-3376 if you have questions. See our Returns Policy for more information.",
        code: 'n/a',
    }); });
    var tagObject = {
        category: {
            primaryCategory: 'error',
        },
        eventInfo: {
            eventName: 'post page error',
            error: errorList,
        },
    };
    pushEventInfo(tagObject);
};
/**
 * Sends analytics tag for when all items are ineligible for return due to hrtv.
 */
export var TagPolicyValidatorAllItemsIneligible = function () {
    var tagObject = {
        category: {
            primaryCategory: 'error',
        },
        eventInfo: {
            eventName: 'post page error',
            error: [
                {
                    type: 'error',
                    displayType: 'inline',
                    pageSection: 'post page error',
                    message: "These items can't be returned online. You can return an item at any The Home Depot store, or call Customer Support at 1-800-430-3376 if you have questions. See our Returns Policy for more information.",
                    code: 'n/a',
                },
            ],
        },
    };
    pushEventInfo(tagObject);
};
/**
 * Tagging for when Policy Validator returns a multi address return
 * @param errorResponse - Axios error response
 */
export var TagPolicyValidatorMultiAddress = function () {
    var tagObject = {
        category: {
            primaryCategory: 'error',
        },
        eventInfo: {
            eventName: 'post page error',
            error: [
                {
                    type: 'advisory',
                    displayType: 'inline',
                    pageSection: 'drop off boxes',
                    message: 'Your items will need to be returned to different locations. Please select the amount of boxes needed per return destination.',
                    code: 'n/a',
                },
            ],
        },
    };
    pushEventInfo(tagObject);
};
/**
 *****************************************************
 *  LORP CONTACT DETAILS FORM TAGGING
 *****************************************************
 */
/**
 * Tagging for when the user opens the Contact Information form for LORP returns
 */
export var TagOnOpenContactInformationForm = function () {
    var tagObject = {
        category: {
            primaryCategory: 'overlay',
        },
        eventInfo: {
            eventName: 'large item pickup contact overlay',
        },
    };
    pushEventInfo(tagObject);
};
/**
 * Tagging for when the user completes the Contact Information form for LORP returns
 */
export var TagOnCompleteContactInformationForm = function () {
    var tagObject = {
        category: {
            primaryCategory: 'button submission',
        },
        eventInfo: {
            eventName: 'my account settings',
        },
        myAccount: {
            settings: 'large item pickup contact overlay: update button',
        },
    };
    pushEventInfo(tagObject);
};
/**
 *****************************************************
 *  REFUND METHOD TAGGING
 *****************************************************
 */
/**
 * Tagging for when the user submits the refund method selection
 * @param paymentMethod - Payment method chosen, ej: "VISA"
 */
export var TagRefundMethodButtonSubmission = function (paymentMethod, militaryExchange) {
    var tagObject = {
        category: {
            primaryCategory: 'button submission',
        },
        eventInfo: {
            eventName: 'my account settings',
        },
        myAccount: {
            settings: 'refund payment method selection: ' + paymentMethod,
        },
        exchange: militaryExchange,
    };
    pushEventInfo(tagObject);
};
/**
 *****************************************************
 *  PRINT RETURN LABEL TAGGING
 *****************************************************
 */
/**
 * Tagging for when the user clicks the print return label button
 */
export var TagShippingLabelButtonSubmission = function (militaryExchange) {
    var tagObject = {
        category: {
            primaryCategory: 'button submission',
        },
        eventInfo: {
            eventName: 'my account settings',
        },
        myAccount: {
            settings: 'print a return label button clicked',
        },
        exchange: militaryExchange,
    };
    pushEventInfo(tagObject);
};
/**
 * Tagging for when the user received an error while trying to print a return label
 * @param type - the type of error, ej: "TRYAGAIN" | "EXPIRED"
 * @param error - the Axios error object. Can be either the legacy nested one or the new generic one. Can also be null if the error is not network related.
 */
export var TagPrintReturnLabelBrowserError = function (type, error) {
    if (error === void 0) { error = null; }
    var _a = error || {}, response = _a.response, code = _a.code;
    var isNetworkError = (response === null || response === void 0 ? void 0 : response.status) === 500 ||
        code === 'ERR_NETWORK' ||
        code === 'ECONNABORTED';
    var errorData = error ? getAPIErrorData(error) : null;
    var PrintLabelAlert = PrintLabelAlerts({
        error: { type: type, retryable: false },
    });
    var message = getTextFromComponent(PrintLabelAlert || { props: { children: '' } }) ||
        'We’re sorry. There was a temporary technical error. Please try again shortly.';
    var tagObject = {
        category: {
            primaryCategory: 'error',
        },
        eventInfo: {
            eventName: 'post page error',
            error: [
                {
                    type: isNetworkError ? 'system' : 'error',
                    displayType: 'inline',
                    pageSection: 'print shipping label',
                    message: message,
                    code: errorData ? errorData === null || errorData === void 0 ? void 0 : errorData.code : 'n/a',
                },
            ],
        },
    };
    pushEventInfo(tagObject);
};
/**
 *****************************************************
 *  GENERIC API ERROR TAGGING
 *****************************************************
 */
/**
 * Sends an analytics tag event for any network-related API error.
 * @param errorResponse generic AxiosError response
 */
export var TagApiError = function (errorResponse) {
    var _a = errorResponse || {}, response = _a.response, code = _a.code;
    var isNetworkError = (response === null || response === void 0 ? void 0 : response.status) === 500 ||
        code === 'ERR_NETWORK' ||
        code === 'ECONNABORTED';
    // Currently we only ever have one error code max, so we just grab the first one if it exists
    var errorData = getAPIErrorData(errorResponse);
    var TECHNICAL_ERROR_MESSAGE = 'We’re sorry. There was a temporary technical error. Please try again shortly.';
    var tagObject = {
        category: {
            primaryCategory: 'error',
        },
        eventInfo: {
            eventName: 'post page error',
            error: [
                {
                    type: isNetworkError ? 'system' : 'error',
                    displayType: 'inline',
                    pageSection: 'post page error',
                    message: TECHNICAL_ERROR_MESSAGE,
                    code: errorData === null || errorData === void 0 ? void 0 : errorData.code,
                },
            ],
        },
    };
    pushEventInfo(tagObject);
};
/**
 *****************************************************
 *  FINALIZE RETURN TAGGING
 *****************************************************
 */
/**
 * Sends an analytics tag event for a Finalize Return load
 */
export var TagOnFinalizeReturnPage = function (method, militaryExchange) {
    var user = {
        profile: {
            exchange: militaryExchange,
        },
    };
    var methodChosen = method === RETURN_METHOD.IN_STORE ? 'in store' : 'ups';
    var pageDetails = __assign(__assign({}, defaultPageInfo), { pageInfo: __assign(__assign({}, defaultPageInfo.pageInfo), { pageName: "account>purchase history>order details>return items>".concat(methodChosen, " finalize return"), pageType: 'maml: return items' }) });
    setPageDetails(pageDetails, { user: user });
};
/**
 * Map payment method display/value or tender type to analytics account type code.
 */
export var getAnalyticsAccountType = function (paymentMethod) {
    if (paymentMethod === void 0) { paymentMethod = ''; }
    var normalized = (paymentMethod || '').toUpperCase();
    var cardMap = {
        VISA: 'visa',
        VI: 'visa',
        MC: 'mc',
        AMEX: 'amex',
        DISC: 'disc',
        HDCON: 'hdcon',
        HDCOM: 'hdcom',
        PTC: 'protrade',
    };
    return cardMap[normalized];
};
/**
 * Sends an analytics tag event for a successful finalize return API call.
 * @param orderDetailsV2 - The order details session data.
 * @param selectedReturnItems - The selected items to return.
 */
export var TagOnSuccessfulReturn = function (orderDetailsV2, selectedReturnItems, method, militaryExchange) {
    var _a;
    if (selectedReturnItems === void 0) { selectedReturnItems = []; }
    var user = {
        profile: {
            exchange: militaryExchange,
        },
    };
    var returnMethodAnalyticsValue = (_a = {},
        _a[RETURN_METHOD.IN_STORE] = 'in store',
        _a[RETURN_METHOD.UPS_DROP_OFF] = 'ups drop-off',
        _a[RETURN_METHOD.LORP] = 'large order return',
        _a);
    var methodChosen = returnMethodAnalyticsValue[method] || 'unknown return method';
    var orderNumber = (orderDetailsV2 || {}).orderNumber;
    var returnSummaryPageInfo = {
        category: {
            primaryCategory: 'account',
            subcategory1: 'account>purchase history',
            subcategory2: 'account>purchase history>order details>return summary',
        },
        pageInfo: {
            pageName: 'account>purchase history>order details>return summary',
            pageType: 'maml: return items',
            pageLoadEvent: 'confirm return',
        },
    };
    var postOrderReturn = {
        item: selectedReturnItems === null || selectedReturnItems === void 0 ? void 0 : selectedReturnItems.map(function (item) {
            var _a, _b, _c, _d, _e, _f, _g;
            return ({
                returnMethod: methodChosen,
                returnPaymentMethod: (_c = getAnalyticsAccountType((_b = (_a = orderDetailsV2.tenders) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.type)) !== null && _c !== void 0 ? _c : (_e = (_d = orderDetailsV2.tenders) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.type,
                productInfo: {
                    sku: item.sku || 0,
                    superSku: item.omsId || 0,
                    productName: ((_f = item.brandName) !== null && _f !== void 0 ? _f : '') + ((_g = item.itemDescription) !== null && _g !== void 0 ? _g : ''),
                },
                price: {
                    // price reflects quantity too, according to the doc
                    basePrice: item.totalPrice || 0,
                },
                quantity: item.returnQuantity || 0,
            });
        }),
        transactionID: orderNumber || '',
    };
    setPageDetails(returnSummaryPageInfo, { postOrderReturn: postOrderReturn, user: user });
};
/**
 * Tag on finalize return error
 * @param errorResponse generic AxiosError response
 */
export var TagFinalizeReturnApiError = function (errorResponse) {
    var _a = errorResponse || {}, response = _a.response, code = _a.code;
    var isNetworkError = (response === null || response === void 0 ? void 0 : response.status) === 500 ||
        code === 'ERR_NETWORK' ||
        code === 'ECONNABORTED';
    // Currently we only ever have one error code max, so we just grab the first one if it exists
    var errorData = getAPIErrorData(errorResponse);
    var message = finalizeReturnErrorMessage[(errorData === null || errorData === void 0 ? void 0 : errorData.code) || 'DEFAULT'];
    var tagObject = {
        category: {
            primaryCategory: 'error',
        },
        eventInfo: {
            eventName: 'post page error',
            error: [
                {
                    type: isNetworkError ? 'system' : 'error',
                    displayType: 'inline',
                    // TODO: confirm if this will change? to have specific, finalize return error page section
                    pageSection: 'post page error',
                    message: message,
                    code: errorData === null || errorData === void 0 ? void 0 : errorData.code,
                },
            ],
        },
    };
    pushEventInfo(tagObject);
};
/**
 *****************************************************
 *  RETURN SUMMARY TAGGING
 *****************************************************
 */
/**
 * Sends an analytics tag event for viewing the Return Summary page
 */
export var TagOnReturnSummaryPage = function () {
    var returnSummaryPageInfo = {
        category: {
            primaryCategory: 'account',
            subcategory1: 'account>purchase history',
            subcategory2: 'account>purchase history>order details>return items>return summary',
        },
        pageInfo: {
            pageName: 'account>purchase history>order details>return items>return summary>existing return',
            pageType: 'maml: return items',
        },
    };
    setPageDetails(returnSummaryPageInfo);
};
/**
 * Sends an analytics tag event for viewing the Return Summary page
 */
export var TagUPSBarcode = function () {
    var tagObject = {
        category: {
            primaryCategory: 'button submission',
        },
        eventInfo: {
            eventName: 'my account settings',
        },
        myAccount: {
            settings: 'purchase history: order details: return summary: view ups barcode button',
        },
    };
    setPageDetails(tagObject);
};
